/*
LIME LABS COMMERCE LAYER
Component: PDP V6.3 Editorial Enhancer
Project: La Troupe
Version: 6.3
Status: LLCL-QA

Presentation-only progressive enhancement.
- No catalog/product/variant/inventory mutation.
- Removes legacy inline Page styles only inside the PDP utility accordion DOM.
- Re-composes Size Guide markup without changing its factual source content.
- Converts the complete Care Guide into compact nested disclosures.
- Marks B-18I proven relevant care categories; does not infer missing facts.
- Mounts B-19I collection-backed status as a non-layout-shifting micro-label.
*/

(() => {
  'use strict';

  if (window.__LLCL_PDP_V63_EDITORIAL__) return;
  window.__LLCL_PDP_V63_EDITORIAL__ = true;

  const DETAILS = '[data-ll-pdp-v4-details]';
  const MAIN = '.shopify-section--main-product';

  const isEnglish = () => (document.documentElement.lang || 'es').toLowerCase().startsWith('en');

  const removeLegacyStyles = scope => {
    scope?.querySelectorAll?.('style').forEach(style => style.remove());
  };

  /* ========================================================
     SIZE GUIDE
     ======================================================== */

  const enhanceSizeTab = tab => {
    if (!(tab instanceof Element) || tab.dataset.llV63SizeReady === 'true') return;

    const subtitles = Array.from(tab.children).filter(node => node.classList?.contains('lt-sg-subtitle'));
    const tables = Array.from(tab.children).filter(node => node.classList?.contains('lt-size-guide-table-wrapper'));
    const note = Array.from(tab.children).find(node => node.classList?.contains('lt-sg-note'));

    if (subtitles.length < 2 || tables.length < 2) return;

    const grid = document.createElement('div');
    grid.className = 'll-pdp-v63-size-grid';

    for (let index = 0; index < 2; index += 1) {
      const panel = document.createElement('section');
      panel.className = 'll-pdp-v63-size-panel';
      panel.append(subtitles[index], tables[index]);
      grid.append(panel);
    }

    tab.prepend(grid);

    if (note) {
      note.classList.add('ll-pdp-v63-size-note');
      tab.append(note);
    }

    tab.dataset.llV63SizeReady = 'true';
  };

  const enhanceSizeGuide = scope => {
    scope.querySelectorAll?.('.ll-pdp-v4-accordion--size .accordion__content').forEach(content => {
      removeLegacyStyles(content);
      content.querySelectorAll('.lt-tab-content').forEach(enhanceSizeTab);
    });
  };

  /* ========================================================
     CARE GUIDE
     ======================================================== */

  const careIndexes = root => String(root.dataset.careSections || '')
    .split(',')
    .map(value => Number.parseInt(value.trim(), 10))
    .filter(value => Number.isInteger(value) && value > 0);

  const careDisclosure = (material, isRelevant) => {
    const header = material.querySelector('.lt-care-header');
    const title = header?.querySelector('h4')?.textContent?.trim();
    const iconImages = Array.from(header?.querySelectorAll('.lt-care-icons img') || []);
    const list = material.querySelector('ul');

    if (!title || !list) return null;

    const details = document.createElement('details');
    details.className = 'll-pdp-v63-care-item';
    if (isRelevant) {
      details.classList.add('is-relevant');
      details.open = true;
    }

    const summary = document.createElement('summary');

    const titleNode = document.createElement('span');
    titleNode.className = 'll-pdp-v63-care-item__title';
    titleNode.textContent = title;
    summary.append(titleNode);

    if (iconImages.length) {
      const icons = document.createElement('span');
      icons.className = 'll-pdp-v63-care-item__icons';
      iconImages.forEach(image => icons.append(image.cloneNode(true)));
      summary.append(icons);
    }

    if (isRelevant) {
      const marker = document.createElement('span');
      marker.className = 'll-pdp-v63-care-match';
      marker.textContent = isEnglish() ? 'FOR THIS PIECE' : 'PARA ESTA PIEZA';
      summary.append(marker);
    }

    const body = document.createElement('div');
    body.className = 'll-pdp-v63-care-item__body';
    body.append(list);

    details.append(summary, body);
    return details;
  };

  const enhanceCareGuide = scope => {
    scope.querySelectorAll?.('[data-ll-v63-care]').forEach(root => {
      if (root.dataset.llV63CareReady === 'true') return;

      removeLegacyStyles(root);

      const guide = root.querySelector('.lt-care-guide');
      if (!guide) return;

      const relevant = new Set(careIndexes(root));
      const materials = Array.from(guide.querySelectorAll(':scope > .lt-care-material'));

      materials.forEach((material, index) => {
        const disclosure = careDisclosure(material, relevant.has(index + 1));
        if (disclosure) material.replaceWith(disclosure);
      });

      root.dataset.llV63CareReady = 'true';
    });
  };

  /* ========================================================
     COMMERCE PANEL + B-19I STATUS
     ======================================================== */

  const detailsData = () => document.querySelector(DETAILS);

  const mountMerchStatus = main => {
    const details = detailsData();
    const list = main?.querySelector('.product-info__block-list');
    if (!details || !list) return;

    list.querySelectorAll('.ll-pdp-v63-status').forEach(node => node.remove());

    const label = (details.dataset.merchStatusLabel || '').trim();
    if (!label) return;

    const status = document.createElement('span');
    status.className = 'll-pdp-v63-status';
    status.dataset.status = details.dataset.merchStatus || '';
    status.textContent = label;
    list.append(status);
  };

  const placeInventoryNearVariants = main => {
    const list = main?.querySelector('.product-info__block-list');
    const quantity = list?.querySelector('.product-info__block-item[data-block-type="quantity-selector"]');
    const inventory = list?.querySelector('.product-info__block-item[data-block-type="inventory"]');

    if (!list || !quantity || !inventory) return;
    if (quantity.nextElementSibling !== inventory) quantity.after(inventory);
  };

  const enhanceCommerce = scope => {
    const mains = [];
    if (scope.matches?.(MAIN)) mains.push(scope);
    scope.querySelectorAll?.(MAIN).forEach(main => mains.push(main));

    mains.forEach(main => {
      placeInventoryNearVariants(main);
      mountMerchStatus(main);
    });
  };

  const boot = (scope = document) => {
    enhanceSizeGuide(scope);
    enhanceCareGuide(scope);
    enhanceCommerce(scope);
  };

  document.addEventListener('product:rerender', event => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const main = event.target?.closest?.(MAIN) || document;
        enhanceCommerce(main);
      });
    });
  });

  document.addEventListener('shopify:section:load', event => {
    boot(event.target || document);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else {
    boot(document);
  }
})();
